/**
 * The layer selector above a plugin's init-config body.
 *
 * Two layers are edited here: the platform-wide global row, and one
 * federated partner's row. The organization and account layers are edited
 * on the records they belong to, so the rail names them and says where,
 * rather than pretending they live on this page.
 *
 * Selecting Partner without choosing one shows the picker alone: a partner
 * row is keyed by partner, and there is no sensible default partner.
 *
 * An infra capability is configured at the global and organization layers
 * only (Feature 84, D27): the rail offers Global alone and says so.
 */
import { listFederatedPartners } from '@optum/api-client/endpoints/federated-partners';
import { useQuery } from '@tanstack/react-query';

import { qk } from '../../lib/query-keys.js';
import { Select } from '../../ui/Select.js';

export type EditableLayer = 'global' | 'partner';

const DOWNSTREAM = [
  { label: 'Organization', where: 'edited on the organization record' },
  { label: 'Account', where: 'edited through the client API' },
];
const INFRA_DOWNSTREAM =
  'Organization — edited on the organization record · user and partner layers are not offered';

export function ConfigLayerRail({
  layer,
  onLayerChange,
  partnerId,
  onPartnerChange,
  infra = false,
}: {
  layer: EditableLayer;
  onLayerChange: (layer: EditableLayer) => void;
  partnerId: string | null;
  onPartnerChange: (partnerId: string | null) => void;
  infra?: boolean | undefined;
}) {
  const partners = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
  });
  const options = (partners.data ?? []).filter((p) => p.status !== 'rejected');

  const tab = (value: EditableLayer, label: string) => (
    <button
      type="button"
      onClick={() => onLayerChange(value)}
      aria-pressed={layer === value}
      style={{
        borderRadius: 'var(--radius-control)',
        padding: '5px 11px',
        fontSize: 'var(--text-body-md)',
        cursor: 'pointer',
        ...(layer === value
          ? {
              background: 'var(--fill-action)',
              color: 'var(--text-inverse)',
              fontWeight: 'var(--weight-medium)',
              border: '1px solid transparent',
            }
          : {
              background: 'transparent',
              color: 'var(--text-muted)',
              border: '1px solid var(--border-input)',
            }),
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7 }}>
      <span
        style={{
          fontSize: 'var(--text-meta-lg)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-muted)',
        }}
      >
        Layer
      </span>
      {tab('global', 'Global')}
      {!infra && tab('partner', 'Partner')}
      {!infra && layer === 'partner' && (
        <Select
          searchable
          value={partnerId ?? ''}
          onChange={(value) => {
            onPartnerChange(value || null);
          }}
          options={[
            { value: '', label: 'Choose a partner…' },
            ...options.map((partner) => ({ value: partner.id, label: partner.name })),
          ]}
        />
      )}
      <span
        style={{ marginLeft: 'auto', fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
      >
        {infra ? INFRA_DOWNSTREAM : DOWNSTREAM.map((d) => `${d.label} — ${d.where}`).join(' · ')}
      </span>
    </div>
  );
}
